/* ContactsPanel — Scheduler / AD ▸ Contacts (AJ 2026-10-14). Doc: /app/docs/CONTACTS_PAGE.md.
 *
 * One contact list per sport (tabs from the org's configured sports), in sections
 * whose titles and status-column labels are edited in place. Rows are compressed
 * inline-edit cells (same look as Opponents ▸ Venues): edit, add, delete.
 * A sport's list starts as a copy of the org's contact seed the first time it opens.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Search, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useTheme } from '../../contexts/ThemeContext';
import { orderSports } from '../SchedulerHub';

const API = process.env.REACT_APP_BACKEND_URL;
const errMsg = (e, fb) => e?.response?.data?.detail || e?.message || fb;

export default function ContactsPanel({ orgId }) {
  const { isDark } = useTheme();
  const [sports, setSports] = useState([]);
  const [sport, setSport] = useState('');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState('');
  const sportRef = useRef('');
  const base = `${API}/api/contacts/${orgId}`;

  useEffect(() => {
    if (!orgId) return;
    axios.get(`${API}/api/game-schedule/${orgId}/sports`)
      .then(({ data }) => {
        const list = orderSports(data?.sports || []);
        setSports(list);
        setSport((s) => s || list[0]?.slug || '');
      }).catch(() => setSports([]));
  }, [orgId]);

  const load = useCallback(async () => {
    if (!orgId || !sport) return;
    setLoading(true);
    const want = sport;
    try {
      const { data } = await axios.get(`${base}/${want}`);
      if (sportRef.current === want) setSections(data?.data?.sections || []);   // ignore a late answer for another sport
    } catch (e) { alert(errMsg(e, 'Could not load contacts')); }
    finally { if (sportRef.current === want) setLoading(false); }
  }, [orgId, sport, base]);
  useEffect(() => { sportRef.current = sport; load(); }, [load, sport]);

  const setRow = (sid, rid, patch) => setSections((l) => l.map((s) => (s.id !== sid ? s
    : { ...s, rows: patch === null ? s.rows.filter((r) => r.id !== rid) : s.rows.map((r) => (r.id === rid ? { ...r, ...patch } : r)) })));
  const patchRow = async (s, r, body) => {
    setRow(s.id, r.id, body);
    try { await axios.patch(`${base}/rows/${r.id}`, body); }
    catch (e) { alert(errMsg(e, 'Save failed')); load(); }
  };
  const addRow = async (s) => {
    try {
      const { data } = await axios.post(`${base}/sections/${s.id}/rows`);
      setSections((l) => l.map((x) => (x.id === s.id ? { ...x, rows: [...x.rows, data.data] } : x)));
    } catch (e) { alert(errMsg(e, 'Could not add a row')); }
  };
  const delRow = async (s, r) => {
    if (!window.confirm(`Delete ${r.name || r.contact || 'this row'}?`)) return;
    try { await axios.delete(`${base}/rows/${r.id}`); setRow(s.id, r.id, null); }
    catch (e) { alert(errMsg(e, 'Delete failed')); }
  };
  const patchSection = async (s, body) => {
    setSections((l) => l.map((x) => (x.id === s.id ? { ...x, ...body } : x)));
    try { await axios.patch(`${base}/sections/${s.id}`, body); }
    catch (e) { alert(errMsg(e, 'Save failed')); load(); }
  };
  const addSection = async () => {
    try {
      const { data } = await axios.post(`${base}/${sport}/sections`, { title: 'New section' });
      setSections((l) => [...l, data.data]);
    } catch (e) { alert(errMsg(e, 'Could not add a section')); }
  };

  const k = q.trim().toLowerCase();
  const shown = useMemo(() => sections.map((s) => ({
    ...s,
    visible: !k ? s.rows : s.rows.filter((r) => ['name', 'contact', 'email', 'phone', 'status', 'notes']
      .some((f) => (r[f] || '').toLowerCase().includes(k))),
  })), [sections, k]);

  const th = `px-2 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide ${isDark ? 'text-slate-400' : 'text-slate-500'}`;
  const cell = `w-full bg-transparent rounded px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none ${isDark ? 'text-white hover:border-slate-600' : 'text-slate-900'}`;
  const card = `rounded-lg border ${isDark ? 'bg-slate-800/50 border-slate-700' : 'bg-white border-slate-200'}`;
  const editable = (value, onSave, cls, ph, testid, required) => (
    <input defaultValue={value || ''} key={`${testid}-${value}`} placeholder={ph} className={cls}
      onBlur={(e) => {
        const v = e.target.value.trim();
        if (required && !v) { e.target.value = value || ''; return; }   // a title cannot be blank
        if (v !== (value || '')) onSave(v);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }} data-testid={testid} />
  );
  const text = (s, r, key, ph, w) => (
    <td className={`px-1 py-1 ${w || ''}`}>{editable(r[key], (v) => patchRow(s, r, { [key]: v }), cell, ph, `contact-${key}-${r.id}`)}</td>
  );

  if (!orgId) return null;
  return (
    <div className="space-y-3" data-testid="contacts-panel">
      <div className="flex items-center gap-2 flex-wrap">
        {sports.map((sp) => (
          <Button key={sp.slug} size="sm" variant={sport === sp.slug ? 'default' : 'outline'} onClick={() => setSport(sp.slug)}
            className={sport === sp.slug ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''} data-testid={`contacts-sport-${sp.slug}`}>
            {sp.label}</Button>
        ))}
        <div className="relative w-full sm:w-auto sm:ml-auto">
          <Search className="h-3.5 w-3.5 absolute left-2 top-2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search contacts"
            className={`h-8 w-full sm:w-56 pl-7 pr-2 text-xs rounded border ${isDark ? 'bg-slate-900 border-slate-700 text-white' : 'bg-white border-slate-200'}`}
            data-testid="contacts-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3 w-3 animate-spin" /> Loading contacts…</div>
      ) : shown.map((s) => (
        <div key={s.id} className={`${card} p-3 space-y-2`} data-testid={`contacts-section-${s.id}`}>
          <div className="flex items-center gap-2">
            {editable(s.title, (v) => patchSection(s, { title: v }),
              `bg-transparent rounded px-1 text-sm font-semibold border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none ${isDark ? 'text-white' : 'text-slate-900'}`,
              'Section name', `contacts-section-title-${s.id}`, true)}
            <span className="text-xs text-slate-500">{k ? `${s.visible.length} of ${s.rows.length}` : s.rows.length}</span>
            <Button size="sm" onClick={() => addRow(s)} className="ml-auto bg-amber-600 hover:bg-amber-700 text-white h-8"
              data-testid={`contacts-add-row-${s.id}`}><Plus className="h-4 w-4 mr-1" />Add row</Button>
          </div>
          <div className={`overflow-x-auto rounded border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <table className="w-full text-xs">
              <thead className={isDark ? 'bg-slate-900/60' : 'bg-slate-50'}>
                <tr>
                  <th className={`${th} w-8`} title="Ticked (the sheet's x column)">✓</th>
                  <th className={th}>Organization</th><th className={th}>Contact</th><th className={th}>Email</th><th className={th}>Phone</th>
                  <th className={th}>{editable(s.status_label, (v) => patchSection(s, { status_label: v }),
                    `bg-transparent w-full text-[10px] font-semibold uppercase tracking-wide border border-transparent hover:border-slate-300 focus:border-amber-500 focus:outline-none rounded px-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`,
                    'Status', `contacts-status-label-${s.id}`)}</th>
                  <th className={th}>Notes</th><th className={th} />
                </tr>
              </thead>
              <tbody>
                {s.visible.map((r) => (
                  <tr key={r.id} className={`border-t ${isDark ? 'border-slate-700/60' : 'border-slate-100'}`}>
                    <td className="px-2 py-1"><input type="checkbox" checked={!!r.mark} onChange={(e) => patchRow(s, r, { mark: e.target.checked })}
                      data-testid={`contact-mark-${r.id}`} /></td>
                    {text(s, r, 'name', 'Organization', 'min-w-[170px]')}{text(s, r, 'contact', 'Name', 'min-w-[150px]')}
                    {text(s, r, 'email', 'Email', 'min-w-[180px]')}{text(s, r, 'phone', 'Phone', 'min-w-[110px]')}
                    {text(s, r, 'status', '—', 'min-w-[140px]')}{text(s, r, 'notes', '—', 'min-w-[180px]')}
                    <td className="px-1 py-1">
                      <button type="button" onClick={() => delRow(s, r)} className="p-1 text-red-400 hover:text-red-600" title="Delete row"
                        data-testid={`contact-delete-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {!s.visible.length && (
                  <tr><td colSpan={8} className="px-2 py-3 text-center text-slate-500">{k ? 'No matches' : 'No contacts yet — Add row'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {!loading && sport && (
        <Button size="sm" variant="outline" onClick={addSection} className="h-8" data-testid="contacts-add-section">
          <Plus className="h-4 w-4 mr-1" />Add section</Button>
      )}
    </div>
  );
}
